import { useContext, useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { ContentContext } from 'context/ContentContext';

import { useInt } from 'hooks/useInt';

import { Image } from 'ui/atoms/Image';
import { Icon } from 'ui/atoms/Icon/Icon';
import { Button } from 'ui/atoms/Button/Button';
import { Section } from 'ui/molecules/Section/Section';

import styles from './FeatureCards.module.scss';

import type { FeatureCardItem, FeatureCardProps, FeatureCardsProps as Props } from './FeatureCards.types';

enum AnimationConstants {
  STEP = 32,
  SCALE_FACTOR = 0.1,
  TRANSITION_DURATION = 0.3,
}

const baseClass = 'feature-cards';

const FeatureCard = ({
  id,
  reverse,
  imageSrc,
  title,
  content,
  listItems,
  className,
  index,
  totalCards,
  progressValues,
}: FeatureCardProps) => {
  const { secondaryNavigation, pageVars } = useContext(ContentContext);
  const ref = useRef<HTMLDivElement | null>(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    const event = [
      'scroll',
      () => {
        if (!ref.current) {
          return;
        }

        const rect = ref.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const visible = 1 - Math.min(Math.max(rect.top / windowHeight, 0), 1);

        setProgress(visible);
      },
    ] as const;

    window.addEventListener(...event, { passive: true });

    return () => {
      window.removeEventListener(...event);
    };
  }, []);

  const translateY = index * AnimationConstants.STEP - progress * AnimationConstants.STEP;
  const nextProgress = progressValues[index + 1] || 0;
  const scale = index < totalCards - 1 && nextProgress > 0 ? 1 - nextProgress * AnimationConstants.SCALE_FACTOR : 1;

  return (
    <div
      ref={ref}
      className={cn(
        styles[`${baseClass}-card-wrapper`],
        reverse && styles[`${baseClass}-card-reverse`],
        styles[`${baseClass}-card-block-${id}`],
        className,
      )}
      style={{
        top: 180,
        zIndex: index,
        position: 'sticky',
        transform: `translateY(${translateY}px) scale(${scale})`,
        transition: `transform ${AnimationConstants.TRANSITION_DURATION}s ease-out`,
      }}
    >
      {imageSrc && (
        <div className={cn(styles[`${baseClass}-card-side`], styles[`${baseClass}-card-media`])}>
          <Image width={480} height={680} src={imageSrc} alt={title} />
        </div>
      )}

      <div className={cn(styles[`${baseClass}-card-side`], styles[`${baseClass}-card-info`])}>
        <div className={styles[`${baseClass}-card-inner`]}>
          <h3 className={styles[`${baseClass}-card-title`]}>{title}</h3>

          {content && <div className={styles[`${baseClass}-card-content`]}>{content}</div>}

          {listItems && listItems.length > 0 && (
            <div className={styles[`${baseClass}-card-content`]}>
              <div className={styles[`${baseClass}-content`]}>
                {listItems.map((item, idx) => (
                  <div key={idx} className={styles[`${baseClass}-inner`]}>
                    <Icon size="md" type="check-13" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <Button
          size="xl"
          type="dark"
          route={secondaryNavigation.book_demo.slug}
          className={styles[`${baseClass}-card-cta`]}
        >
          {pageVars.common.book_demo_button}
        </Button>
      </div>
    </div>
  );
};

export const FeatureCards = ({ title, description, items, fullWidthTitle = false, className }: Props) => {
  const { t } = useInt();

  const accurateContent = (
    <div className={styles[`${baseClass}-content`]}>
      <div className={styles[`${baseClass}-inner`]}>
        <Icon size="md" type="check-2" />

        <span>
          <p>{t('In-House Standardization:')}</p>{' '}
          {t('Data comes in different formats globally; we standardize it for seamless integration.')}
        </span>
      </div>

      <div className={styles[`${baseClass}-inner`]}>
        <Icon size="md" type="check-2" />

        <span>
          <p>{t('Multilingual Translation:')}</p>{' '}
          {t('All data is translated to ensure usability across different languages.')}
        </span>
      </div>

      <div className={styles[`${baseClass}-inner`]}>
        <Icon size="md" type="check-2" />

        <span>
          <p>{t('Continuous Updates:')}</p>{' '}
          {t(
            'Updated daily from government registries and official sources, our data provides real-time accuracy and minimizes outdated information.',
          )}
        </span>
      </div>
    </div>
  );

  const integrationContent = (
    <div className={styles[`${baseClass}-content`]}>
      <span className={styles[`${baseClass}-sub-title`]}>
        {t('Our platform delivers detailed ownership structures, including:')}
      </span>

      <div className={styles[`${baseClass}-inner`]}>
        <Icon size="md" type="check-2" />

        <span>
          <p>{t('Flexible APIs:')} </p> {t('Seamlessly integrate our data into your applications and systems.')}
        </span>
      </div>

      <div className={styles[`${baseClass}-inner`]}>
        <Icon size="md" type="check-2" />

        <span>
          <p>{t('CRM and ERP Compatibility:')} </p>{' '}
          {t(
            'Easily sync with your existing Customer Relationship Management and Enterprise Resource Planning systems.',
          )}
        </span>
      </div>

      <div className={styles[`${baseClass}-inner`]}>
        <Icon size="md" type="check-2" />

        <span>
          <p>{t('Batch Data Delivery Files:')} </p>{' '}
          {t('Receive large volumes of data through batch files for bulk processing and analysis.')}
        </span>
      </div>
    </div>
  );

  const benefitsContent = (
    <div className={styles[`${baseClass}-content`]}>
      <div className={styles[`${baseClass}-inner`]}>
        <Icon size="md" type="check-2" />

        <div>
          <p>{t('Consistency & Reliability Across Borders:')}</p>
          <span>
            {t(
              'Trust data that’s standardized and regularly updated to ensure consistency across multiple countries and regions.',
            )}
          </span>
        </div>
      </div>

      <div className={styles[`${baseClass}-inner`]}>
        <Icon size="md" type="check-2" />

        <div>
          <p>{t('Improved Operational Efficiency')}</p>
          <span>
            {t(
              'Reduce errors, automate processes, and improve customer experiences with accurate and reliable location data.',
            )}
          </span>
        </div>
      </div>

      <div className={styles[`${baseClass}-inner`]}>
        <Icon size="md" type="check-2" />

        <div>
          <p>{t('Flexible, Scalable Solutions')}</p>
          <span>
            {t(
              'Our data solutions support businesses of any size, with customizable plans to meet diverse operational needs.',
            )}
          </span>
        </div>
      </div>
    </div>
  );

  const featureCards: FeatureCardItem[] = [
    {
      id: 1,
      title: 'Accurate and Standardized Data',
      imageSrc: 'img/marketing/1.jpg',
      content: accurateContent,
    },
    {
      id: 2,
      title: 'Easy Integration',
      imageSrc: 'img/marketing/2.jpg',
      content: integrationContent,
      reverse: true,
    },
    {
      id: 3,
      title: 'Benefits of Using Global Database Location Data',
      imageSrc: 'img/marketing/3.jpg',
      content: benefitsContent,
    },
  ];

  const cards = items || featureCards;
  const [progressValues, setProgressValues] = useState(new Array(cards.length).fill(0));

  useEffect(() => {
    const event = [
      'scroll',
      () => {
        const newProgressValues = cards.map((_, idx) => {
          const ref = document.querySelector(`.${styles[`${baseClass}-card-block-${idx + 1}`]}`);
          if (!ref) {
            return 0;
          }

          const rect = ref.getBoundingClientRect();
          const windowHeight = window.innerHeight;

          return 1 - Math.min(Math.max(rect.top / windowHeight, 0), 1);
        });

        setProgressValues(newProgressValues);
      },
    ] as const;

    window.addEventListener(...event, {
      passive: true,
    });

    return () => {
      window.removeEventListener(...event);
    };
  }, [cards]);

  return (
    <Section
      title={title}
      description={description}
      fullWidth={fullWidthTitle}
      className={cn(styles[`${baseClass}-wrapper`], className)}
    >
      {cards.map((block, idx) => (
        <FeatureCard
          key={idx}
          id={block.id}
          title={block.title}
          content={block.content}
          listItems={block.listItems}
          imageSrc={block.imageSrc}
          reverse={block.reverse}
          progressValues={progressValues}
          totalCards={cards.length}
          index={idx}
        />
      ))}
    </Section>
  );
};
